export interface SuperAdminNavItem {
  label: string;
  path: string;
  icon: string;
  description?: string;
}

export interface SuperAdminNavGroup {
  title: string;
  items: SuperAdminNavItem[];
  /** Visual separator after this group */
  dividerAfter?: boolean;
}

/** Primary super admin sidebar navigation */
export const superAdminMenu: SuperAdminNavGroup[] = [
  {
    title: 'Overview',
    items: [
      { label: 'Dashboard', path: '/super-admin', icon: 'SpaceDashboard', description: 'Platform summary' },
    ],
    dividerAfter: true,
  },
  {
    title: 'Tenants',
    items: [
      { label: 'All Tenants', path: '/super-admin/tenants', icon: 'Business', description: 'Companies & billing' },
      { label: 'Pending Approvals', path: '/super-admin/tenants/pending', icon: 'PendingActions', description: 'New registrations' },
      { label: 'Module Access', path: '/super-admin/tenant-modules', icon: 'ViewModule', description: 'Per-tenant modules' },
      { label: 'Report Access', path: '/super-admin/tenant-reports', icon: 'Assessment', description: 'Assign reports to tenants' },
    ],
    dividerAfter: true,
  },
  {
    title: 'Billing',
    items: [
      { label: 'Plans', path: '/super-admin/plans', icon: 'CardMembership', description: 'Subscription tiers' },
      { label: 'Subscriptions', path: '/super-admin/subscriptions', icon: 'Autorenew', description: 'Tenant plans & GST' },
      { label: 'Invoices', path: '/super-admin/invoices', icon: 'Description', description: 'Platform billing' },
      { label: 'Billing Profile', path: '/super-admin/billing-profile', icon: 'Business', description: 'Upload logo, GST & signature' },
      { label: 'Transactions', path: '/super-admin/transactions', icon: 'ReceiptLong', description: 'Payment history' },
    ],
    dividerAfter: true,
  },
  {
    title: 'Platform',
    items: [
      { label: 'Core Modules', path: '/super-admin/modules', icon: 'Widgets', description: 'Global module toggles' },
      { label: 'Report Templates', path: '/super-admin/report-templates', icon: 'Assessment', description: 'Detailed tenant reports' },
      { label: 'Feature Flags', path: '/super-admin/feature-flags', icon: 'Flag', description: 'Product features' },
      { label: 'Field Types', path: '/super-admin/field-types', icon: 'TextFields', description: 'CRM field registry' },
      { label: 'Relationships', path: '/super-admin/relationships', icon: 'AccountTree', description: 'Module links' },
      { label: 'Integrations', path: '/super-admin/integrations', icon: 'Extension', description: 'App marketplace' },
    ],
    dividerAfter: true,
  },
  {
    title: 'Fleet Reference',
    items: [
      { label: 'Vehicle Types', path: '/super-admin/master/vehicle_type', icon: 'LocalShipping' },
      { label: 'Vehicle Makes', path: '/super-admin/master/vehicle_make', icon: 'Build' },
      { label: 'Vehicle Models', path: '/super-admin/master/vehicle_model', icon: 'DirectionsCar' },
    ],
    dividerAfter: true,
  },
  {
    title: 'Security',
    items: [
      { label: 'Admin Users', path: '/super-admin/users', icon: 'Shield', description: 'Super admin accounts' },
      { label: 'Audit & Login Logs', path: '/super-admin/security', icon: 'Security', description: 'Access activity' },
    ],
  },
];

/** Pinned at bottom of super admin sidebar */
export const superAdminFooterNav: SuperAdminNavItem[] = [
  {
    label: 'Platform Settings',
    path: '/super-admin/settings',
    icon: 'Tune',
    description: 'Locale, limits & defaults',
  },
];

/** In-page settings navigation (Platform Settings hub) */
export const superAdminSettingsSections = [
  {
    id: 'regional',
    label: 'Regional & Locale',
    icon: 'Language',
    description: 'Language, currency, timezone defaults',
  },
  {
    id: 'system',
    label: 'System Limits',
    icon: 'Storage',
    description: 'Upload limits and platform caps',
  },
] as const;

export type SuperAdminSettingsSectionId = typeof superAdminSettingsSections[number]['id'];

export const superAdminSettingsLinks: SuperAdminNavItem[] = [
  { label: 'Core Modules', path: '/super-admin/modules', icon: 'Widgets' },
  { label: 'Feature Flags', path: '/super-admin/feature-flags', icon: 'Flag' },
  { label: 'Integrations', path: '/super-admin/integrations', icon: 'Extension' },
  { label: 'Field Types', path: '/super-admin/field-types', icon: 'TextFields' },
  { label: 'Relationship Rules', path: '/super-admin/relationships', icon: 'AccountTree' },
];

export const flattenSuperAdminPaths = () => [
  ...superAdminMenu.flatMap((g) => g.items.map((i) => i.path)),
  ...superAdminFooterNav.map((i) => i.path),
];
